import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../shared/Icon';
import { useNavigate } from 'react-router-dom';
import './OtpVerification.css';
import { apiService } from '../../../services/api';

function OtpVerification({ email }) {
  const navigate = useNavigate();
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');
  const inputRefs = useRef([]);

  const handleChange = useCallback((index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    setOtpDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    setError('');
    setResendSuccess('');

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }, []);

  const handleKeyDown = useCallback((index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }, [otpDigits]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newDigits = ['', '', '', '', '', ''];
      for (let i = 0; i < pasted.length; i++) {
        newDigits[i] = pasted[i];
      }
      setOtpDigits(newDigits);
      // Mirror handleChange: clear a prior error/success so pasting a fresh
      // code re-enables Continue instead of leaving it disabled by !!error.
      setError('');
      setResendSuccess('');
      const focusIdx = Math.min(pasted.length, 5);
      inputRefs.current[focusIdx]?.focus();
    }
  }, []);

  const handleVerify = async (e) => {
    if (e) e.preventDefault();
    const code = otpDigits.join('');
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }
    setVerifying(true);
    setError('');
    try {
      const data = await apiService.otpVerify(email, code);
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        navigate('/', { replace: true });
        window.location.reload();
        return;
      }
      setError('Invalid or expired code. Please try again.');
    } catch (err) {
      setError(err.message || 'Unable to verify code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  // Auto-submit when all 6 digits are filled
  useEffect(() => {
    if (otpDigits.every((d) => d !== '') && !verifying) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpDigits]);

  const handleResendCode = async () => {
    setResending(true);
    setError('');
    setResendSuccess('');
    try {
      await apiService.otpResend(email);
      setResendSuccess('A new code has been sent to your email.');
      setOtpDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.message || 'Unable to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const renderBox = (digit, i) => (
    <input
      key={i}
      ref={(el) => (inputRefs.current[i] = el)}
      type="text"
      inputMode="numeric"
      maxLength={1}
      aria-label={`Digit ${i + 1}`}
      className={`otp-input resui-input${error ? ' resui-input--error' : ''}`}
      value={digit}
      onChange={(e) => handleChange(i, e.target.value)}
      onKeyDown={(e) => handleKeyDown(i, e)}
      onPaste={i === 0 ? handlePaste : undefined}
      autoFocus={i === 0}
      disabled={verifying}
    />
  );

  return (
    <>
      <p className="otp-content">
        An OTP code has been emailed to <strong>{email.trim()}</strong>. Enter the code
        here or select the button in the email to continue.{' '}
        <strong>If you don't see the email, check your spam or junk folder.</strong>
      </p>

      {error && (
        <div className="resui-banner resui-banner--error" role="alert">
          <Icon name="error" />
          <span className="resui-banner-text">{error}</span>
        </div>
      )}
      {resendSuccess && (
        <div className="resui-banner resui-banner--success" role="status">
          <Icon name="check_circle" />
          <span className="resui-banner-text">{resendSuccess}</span>
        </div>
      )}

      <form onSubmit={handleVerify} className="otp-form">
        <div className="otp-inputs">
          {otpDigits.slice(0, 3).map((digit, i) => renderBox(digit, i))}
          <span className="otp-dash">-</span>
          {otpDigits.slice(3, 6).map((digit, i) => renderBox(digit, i + 3))}
        </div>
        <button
          type="submit"
          className="resui-btn resui-btn--primary otp-continue-btn"
          disabled={verifying || otpDigits.some((d) => d === '') || !!error}
        >
          {verifying ? 'Verifying…' : 'Continue'}
        </button>
      </form>

      <button
        type="button"
        className="otp-resend-link"
        onClick={handleResendCode}
        disabled={resending}
      >
        {resending ? 'Sending…' : 'Request a new code'}
      </button>
    </>
  );
}

export default OtpVerification;
